import { useState, useEffect } from "react";
import axios from "axios";
import { Users, Search, TrendingUp, Crown, ArrowLeft, Calendar, LogOut, Eye, BarChart3, X, Mail, ChevronDown, ChevronUp, Briefcase, Sun, Moon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@/contexts/ThemeContext";

const API = `${window.location.origin}/api`;

function CollapsibleSection({ title, icon: Icon, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-200 dark:border-gray-800 rounded-xl bg-white dark:bg-gray-900 overflow-hidden transition-colors mb-3">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors text-left"
        data-testid={`section-toggle-${title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon className="h-4 w-4 text-gray-400 dark:text-gray-500" />}
          <span className="font-semibold text-gray-900 dark:text-white text-sm">{title}</span>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
      </button>
      {open && <div className="px-4 pb-4 border-t border-gray-100 dark:border-gray-800 pt-4">{children}</div>}
    </div>
  );
}

function AdminAnalytics({ user, onLogout }) {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [analytics, setAnalytics] = useState(null);
  const [siteVisits, setSiteVisits] = useState(null);
  const [recentUsers, setRecentUsers] = useState([]);
  const [recentLookups, setRecentLookups] = useState([]);
  const [enterpriseLeads, setEnterpriseLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUserModal, setShowUserModal] = useState(false);
  const [modalUsers, setModalUsers] = useState([]);
  const [modalTitle, setModalTitle] = useState("");
  const [loadingUsers, setLoadingUsers] = useState(false);

  const fetchUsersByType = async (type) => {
    setLoadingUsers(true);
    try {
      const res = await axios.get(`${API}/admin/users-by-subscription?subscription_type=${type}`, { withCredentials: true });
      setModalUsers(res.data);
      setModalTitle(type === 'premium' ? 'Premium Users' : 'Free Users');
      setShowUserModal(true);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const [analyticsRes, siteVisitsRes, usersRes, lookupsRes, leadsRes] = await Promise.all([
        axios.get(`${API}/admin/analytics`, { withCredentials: true }),
        axios.get(`${API}/admin/site-visits`, { withCredentials: true }),
        axios.get(`${API}/admin/recent-users?limit=10`, { withCredentials: true }),
        axios.get(`${API}/admin/recent-lookups?limit=20`, { withCredentials: true }),
        axios.get(`${API}/admin/enterprise-leads`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setAnalytics(analyticsRes.data);
      setSiteVisits(siteVisitsRes.data);
      setRecentUsers(usersRes.data);
      setRecentLookups(lookupsRes.data);
      setEnterpriseLeads(leadsRes.data || []);
    } catch (error) {
      console.error("Error fetching analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  const fmt = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const pendingLeads = enterpriseLeads.filter(l => l.status === 'new' || l.status === 'pending');

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center transition-colors">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 transition-colors">
      {/* Header */}
      <header className="border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 transition-colors">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white" data-testid="back-home-btn">
              <ArrowLeft className="h-4 w-4 mr-1" /> Home
            </Button>
            <h1 className="text-lg font-bold text-gray-900 dark:text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Analytics</h1>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={toggleTheme} className="p-2 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors" data-testid="theme-toggle-btn">
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <span className="text-gray-500 dark:text-gray-400 text-sm hidden sm:inline">{user?.name}</span>
            <Button onClick={onLogout} variant="outline" size="sm" className="border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800" data-testid="logout-btn">
              <LogOut className="h-4 w-4 mr-1" /> Sign Out
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-4xl">

        {/* ═══════════ TOP 3 HERO METRICS ═══════════ */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8" data-testid="hero-metrics">
          {/* Today's Visitors */}
          <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800" data-testid="metric-today-visitors">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-1">
                <Eye className="h-4 w-4 text-blue-500" />
                <span className="text-gray-500 dark:text-gray-400 text-xs font-medium uppercase tracking-wider">Today's Visitors</span>
              </div>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">{siteVisits?.today_visits || 0}</p>
              <p className="text-gray-400 dark:text-gray-500 text-xs mt-1">{siteVisits?.week_visits || 0} this week · {siteVisits?.month_visits || 0} this month</p>
            </CardContent>
          </Card>

          {/* Today's Lookups */}
          <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800" data-testid="metric-today-lookups">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-1">
                <Search className="h-4 w-4 text-yellow-500" />
                <span className="text-gray-500 dark:text-gray-400 text-xs font-medium uppercase tracking-wider">Today's Lookups</span>
              </div>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">{analytics?.lookups_today || 0}</p>
              <p className="text-gray-400 dark:text-gray-500 text-xs mt-1">{analytics?.total_lookups || 0} all time</p>
            </CardContent>
          </Card>

          {/* Enterprise Enquiries */}
          <Card className={`bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 ${pendingLeads.length > 0 ? 'ring-2 ring-yellow-400 dark:ring-yellow-500/50' : ''}`} data-testid="metric-enterprise-leads">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-1">
                <Briefcase className="h-4 w-4 text-emerald-500" />
                <span className="text-gray-500 dark:text-gray-400 text-xs font-medium uppercase tracking-wider">Enquiries</span>
              </div>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">
                {pendingLeads.length}
                {pendingLeads.length > 0 && <span className="text-sm font-normal text-yellow-600 dark:text-yellow-500 ml-2">new</span>}
              </p>
              <p className="text-gray-400 dark:text-gray-500 text-xs mt-1">{enterpriseLeads.length} total leads</p>
            </CardContent>
          </Card>
        </div>

        {/* ═══════════ PENDING LEADS (if any) ═══════════ */}
        {pendingLeads.length > 0 && (
          <div className="mb-6 bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800/30 rounded-xl p-4" data-testid="pending-leads">
            <p className="text-yellow-800 dark:text-yellow-400 font-semibold text-sm mb-3">New enquiries awaiting response</p>
            {pendingLeads.map((lead, i) => (
              <div key={lead.id || i} className="flex items-center justify-between py-2 border-b border-yellow-100 dark:border-yellow-800/20 last:border-0">
                <div>
                  <p className="text-gray-900 dark:text-white text-sm font-medium">{lead.company_name}</p>
                  <p className="text-gray-500 dark:text-gray-400 text-xs">{lead.contact_email} · Fleet: {lead.fleet_size || 'N/A'}</p>
                </div>
                <span className="text-gray-400 dark:text-gray-600 text-xs">{fmt(lead.created_at)}</span>
              </div>
            ))}
          </div>
        )}

        {/* ═══════════ COLLAPSIBLE SECTIONS ═══════════ */}

        <CollapsibleSection title="Users Overview" icon={Users}>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{analytics?.total_users || 0}</p>
              <p className="text-gray-400 dark:text-gray-500 text-xs">Total</p>
            </div>
            <div className="text-center cursor-pointer" onClick={() => fetchUsersByType('premium')} data-testid="premium-users-click">
              <p className="text-2xl font-bold text-yellow-600">{analytics?.premium_users || 0}</p>
              <p className="text-yellow-600 dark:text-yellow-500 text-xs hover:underline">Premium</p>
            </div>
            <div className="text-center cursor-pointer" onClick={() => fetchUsersByType('free')} data-testid="free-users-click">
              <p className="text-2xl font-bold text-gray-500 dark:text-gray-400">{analytics?.free_users || 0}</p>
              <p className="text-gray-400 dark:text-gray-500 text-xs hover:underline">Free</p>
            </div>
          </div>
          <p className="text-gray-400 dark:text-gray-500 text-xs"><span className="text-emerald-500 font-semibold">{analytics?.recent_signups || 0}</span> new signups in the last 7 days</p>
        </CollapsibleSection>

        <CollapsibleSection title="Daily Visitors (Last 10 Days)" icon={BarChart3}>
          <div className="space-y-1.5">
            {siteVisits?.daily_visits?.slice(-10).map((day) => (
              <div key={day.date} className="flex items-center gap-3">
                <span className="text-gray-500 dark:text-gray-400 text-xs w-20">{day.date}</span>
                <div className="flex-1 bg-gray-100 dark:bg-gray-800 rounded-full h-4 overflow-hidden">
                  <div
                    className="bg-blue-500/70 h-full rounded-full"
                    style={{ width: `${Math.min(100, (day.visits / (Math.max(...siteVisits.daily_visits.map(d => d.visits)) || 1)) * 100)}%` }}
                  />
                </div>
                <span className="text-gray-900 dark:text-white text-xs font-medium w-10 text-right">{day.visits}</span>
              </div>
            ))}
            {(!siteVisits?.daily_visits || siteVisits.daily_visits.length === 0) && (
              <p className="text-gray-400 dark:text-gray-500 text-xs">No visit data yet</p>
            )}
          </div>
        </CollapsibleSection>

        <CollapsibleSection title="Daily Lookups (Last 10 Days)" icon={Search}>
          <div className="space-y-1.5">
            {analytics?.daily_lookups?.slice(-10).map((day) => (
              <div key={day._id} className="flex items-center gap-3">
                <span className="text-gray-500 dark:text-gray-400 text-xs w-20">{day._id}</span>
                <div className="flex-1 bg-gray-100 dark:bg-gray-800 rounded-full h-4 overflow-hidden">
                  <div
                    className="bg-yellow-500/70 h-full rounded-full"
                    style={{ width: `${Math.min(100, (day.count / (Math.max(...analytics.daily_lookups.map(d => d.count)) || 1)) * 100)}%` }}
                  />
                </div>
                <span className="text-gray-900 dark:text-white text-xs font-medium w-10 text-right">{day.count}</span>
              </div>
            ))}
            {(!analytics?.daily_lookups || analytics.daily_lookups.length === 0) && (
              <p className="text-gray-400 dark:text-gray-500 text-xs">No lookup data yet</p>
            )}
          </div>
        </CollapsibleSection>

        <CollapsibleSection title="Daily Signups (Last 10 Days)" icon={Calendar}>
          <div className="space-y-1.5">
            {analytics?.daily_signups?.slice(-10).map((day) => (
              <div key={day._id} className="flex items-center gap-3">
                <span className="text-gray-500 dark:text-gray-400 text-xs w-20">{day._id}</span>
                <div className="flex-1 bg-gray-100 dark:bg-gray-800 rounded-full h-4 overflow-hidden">
                  <div
                    className="bg-emerald-500/70 h-full rounded-full"
                    style={{ width: `${Math.min(100, (day.count / (Math.max(...analytics.daily_signups.map(d => d.count)) || 1)) * 100)}%` }}
                  />
                </div>
                <span className="text-gray-900 dark:text-white text-xs font-medium w-10 text-right">{day.count}</span>
              </div>
            ))}
            {(!analytics?.daily_signups || analytics.daily_signups.length === 0) && (
              <p className="text-gray-400 dark:text-gray-500 text-xs">No signup data yet</p>
            )}
          </div>
        </CollapsibleSection>

        <CollapsibleSection title="Top Pages (Last 30 Days)" icon={Eye}>
          <div className="space-y-2">
            {siteVisits?.top_pages?.map((page, i) => (
              <div key={i} className="flex items-center justify-between">
                <span className="text-gray-700 dark:text-gray-300 text-sm truncate max-w-[250px]">{page.page || '/'}</span>
                <span className="text-gray-900 dark:text-white text-sm font-medium">{page.views}</span>
              </div>
            ))}
            {(!siteVisits?.top_pages || siteVisits.top_pages.length === 0) && (
              <p className="text-gray-400 dark:text-gray-500 text-xs">No page data yet</p>
            )}
          </div>
        </CollapsibleSection>

        <CollapsibleSection title="Recent Users" icon={Users}>
          <div className="overflow-x-auto -mx-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-800">
                  <th className="text-left py-2 px-4 text-gray-500 dark:text-gray-400 font-medium text-xs">Name</th>
                  <th className="text-left py-2 px-4 text-gray-500 dark:text-gray-400 font-medium text-xs">Email</th>
                  <th className="text-left py-2 px-4 text-gray-500 dark:text-gray-400 font-medium text-xs">Status</th>
                  <th className="text-left py-2 px-4 text-gray-500 dark:text-gray-400 font-medium text-xs">Joined</th>
                </tr>
              </thead>
              <tbody>
                {recentUsers.map((u) => (
                  <tr key={u.id} className="border-b border-gray-50 dark:border-gray-800/50">
                    <td className="py-2 px-4 text-gray-900 dark:text-white">{u.name}</td>
                    <td className="py-2 px-4 text-gray-500 dark:text-gray-400">{u.email}</td>
                    <td className="py-2 px-4">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${u.subscription_status === 'premium' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'}`}>
                        {u.subscription_status}
                      </span>
                    </td>
                    <td className="py-2 px-4 text-gray-400 dark:text-gray-500 text-xs">{fmt(u.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CollapsibleSection>

        <CollapsibleSection title="Recent Lookups" icon={Search}>
          <div className="overflow-x-auto -mx-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-800">
                  <th className="text-left py-2 px-4 text-gray-500 dark:text-gray-400 font-medium text-xs">Reg</th>
                  <th className="text-left py-2 px-4 text-gray-500 dark:text-gray-400 font-medium text-xs">User</th>
                  <th className="text-left py-2 px-4 text-gray-500 dark:text-gray-400 font-medium text-xs">Make</th>
                  <th className="text-left py-2 px-4 text-gray-500 dark:text-gray-400 font-medium text-xs">Tax</th>
                  <th className="text-left py-2 px-4 text-gray-500 dark:text-gray-400 font-medium text-xs">MOT</th>
                  <th className="text-left py-2 px-4 text-gray-500 dark:text-gray-400 font-medium text-xs">Time</th>
                </tr>
              </thead>
              <tbody>
                {recentLookups.map((l) => (
                  <tr key={l.id} className="border-b border-gray-50 dark:border-gray-800/50">
                    <td className="py-2 px-4 text-gray-900 dark:text-white font-semibold">{l.registration}</td>
                    <td className="py-2 px-4">
                      <span className={l.user_name === 'Guest' ? 'text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-300'}>{l.user_name || 'Guest'}</span>
                    </td>
                    <td className="py-2 px-4 text-gray-500 dark:text-gray-400">{l.make || 'N/A'}</td>
                    <td className="py-2 px-4">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${l.tax_status === 'Taxed' ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400' : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'}`}>
                        {l.tax_status}
                      </span>
                    </td>
                    <td className="py-2 px-4">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${l.mot_status === 'Valid' ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400' : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'}`}>
                        {l.mot_status}
                      </span>
                    </td>
                    <td className="py-2 px-4 text-gray-400 dark:text-gray-500 text-xs">{fmt(l.timestamp)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CollapsibleSection>

        <CollapsibleSection title="All Enterprise Leads" icon={Briefcase}>
          {enterpriseLeads.length === 0 ? (
            <p className="text-gray-400 dark:text-gray-500 text-xs">No enterprise leads yet</p>
          ) : (
            <div className="space-y-3">
              {enterpriseLeads.map((lead, i) => (
                <div key={lead.id || i} className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-800 last:border-0">
                  <div>
                    <p className="text-gray-900 dark:text-white text-sm font-medium">{lead.company_name}</p>
                    <p className="text-gray-500 dark:text-gray-400 text-xs">{lead.contact_email} · Fleet: {lead.fleet_size || 'N/A'}</p>
                  </div>
                  <div className="text-right">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${lead.status === 'new' || lead.status === 'pending' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400' : lead.status === 'contacted' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'}`}>
                      {lead.status}
                    </span>
                    <p className="text-gray-400 dark:text-gray-600 text-xs mt-0.5">{fmt(lead.created_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CollapsibleSection>
      </div>

      {/* User List Modal */}
      {showUserModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
              <h2 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                {modalTitle === 'Premium Users' ? <Crown className="h-5 w-5 text-yellow-600" /> : <Users className="h-5 w-5 text-gray-500" />}
                {modalTitle}
                <span className="text-sm font-normal text-gray-400">({modalUsers.length})</span>
              </h2>
              <Button variant="ghost" size="sm" onClick={() => setShowUserModal(false)}><X className="h-4 w-4" /></Button>
            </div>
            <div className="overflow-y-auto max-h-[60vh]">
              {loadingUsers ? (
                <div className="p-8 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500 mx-auto" /></div>
              ) : modalUsers.length === 0 ? (
                <p className="p-8 text-center text-gray-400 dark:text-gray-500">No users found</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                    <tr>
                      <th className="text-left py-2 px-4 text-gray-500 dark:text-gray-400 font-medium text-xs">Name</th>
                      <th className="text-left py-2 px-4 text-gray-500 dark:text-gray-400 font-medium text-xs">Email</th>
                      <th className="text-left py-2 px-4 text-gray-500 dark:text-gray-400 font-medium text-xs">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modalUsers.map((u, i) => (
                      <tr key={u.id || i} className="border-b border-gray-100 dark:border-gray-800">
                        <td className="py-2 px-4 text-gray-900 dark:text-white">{u.name || '—'}</td>
                        <td className="py-2 px-4"><a href={`mailto:${u.email}`} className="text-blue-500 hover:underline text-xs">{u.email}</a></td>
                        <td className="py-2 px-4 text-gray-400 dark:text-gray-500 text-xs">{fmt(u.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminAnalytics;
